import { DifficultyId, GameMode, INTERNAL_HEIGHT, INTERNAL_WIDTH } from '../constants';
import { PURCHASES } from '../data/purchases';
import { AudioEngine, type AudioSettings } from '../audio/AudioEngine';
import { LEGACY_DUCK_BY_SFX_ID } from '../audio/r2/GameplaySfxDirector';
import { InputManager, isNativeUiControl } from '../input/InputManager';
import { clamp } from '../math/scalar';
import { createMobileFramePhaseSampler } from '../performance/FramePhaseSampler';
import { isMobilePerformanceDevice } from '../platform/mobilePerformance';
import { PixiRenderer } from '../render/pixi/PixiRenderer';
import type { RendererPort, RendererSettings } from '../render/port';
import { R2GameRenderer } from '../render/r2/R2GameRenderer';
import { isR2RenderEnabled } from '../render/r2/flag';
import { CoreBeamId } from '../weapons/definitions';
import { GameWorld, WorldEvent } from '../world/GameWorld';
import { ComboFeelAdapter } from './ComboFeelAdapter';
import { isGodModeEnabled } from './debugFlags';
import { FixedStepLoop } from './FixedStepLoop';
import { RuntimeUIStore } from './RuntimeUIStore';
import { StageRankingStore } from './StageRankingStore';

/** PointerEvent.button の主ボタン。 */
const PRIMARY_POINTER_BUTTON = 0;

/**
 * ?god=1 のときだけ効く武器デバッグキー。InputManager の KEY_ACTION と衝突しない
 * コードだけを使い、無指定時は listener 自体が即 return するので通常操作は同一。
 */
const GOD_CORE_KEY: Readonly<Record<string, CoreBeamId>> = Object.freeze({
  Digit1: CoreBeamId.Pulse,
  Digit2: CoreBeamId.Arc,
  Digit3: CoreBeamId.Prism,
  Digit4: CoreBeamId.Lattice,
});
const GOD_LENS_KEY = 'KeyZ';
const GOD_DEVICE_KEY = 'KeyX';
const GOD_WEAPON_LEVEL_KEY = 'KeyC';
/** R2道中は初期版の25%。敵wave密度は初期比約4倍、直前版比約2倍。 */
const R2_STAGE_TIMELINE_SCALE = 0.25;
const R2_DEFAULT_MUSIC_LEVEL = 0.82;
const R2_DEFAULT_SFX_LEVEL = 0.7;
const R2_BOSS_HP_MULTIPLIER = 2.4;
const R2_BOSS_ATTACK_INTERVAL_SCALE = 0.62;
const R2_BOSS_PROJECTILE_SPEED_SCALE = 1.12;

export interface GameSettings extends AudioSettings, RendererSettings {
  rumble: boolean;
  touchOpacity: number;
}

export const DEFAULT_SETTINGS: GameSettings = Object.freeze({
  master: 0.78,
  music: 0.56,
  sfx: 0.82,
  shake: 1,
  flashReduction: false,
  rumble: true,
  touchOpacity: 0.72,
});

export class GameRuntime {
  readonly world = new GameWorld();
  readonly input = new InputManager();
  private readonly r2Enabled = isR2RenderEnabled();
  private readonly mobilePerformanceMode = this.r2Enabled && isMobilePerformanceDevice();
  /**
   * ?r2=0 は引数なしと同じ legacy profile のまま。R2 だけが近未来ミニマル
   * プロファイルを選ぶため、凍結した旧経路の可聴挙動には影響しない。
   */
  readonly audio = new AudioEngine(this.r2Enabled ? 'r2-minimal' : 'legacy');
  // The R2 path is selected here and nowhere else. Both classes are held as
  // RendererPort, the surface this runtime and FixedStepLoop actually call, so
  // the branch needs no cast: PixiRenderer satisfies the port structurally and
  // R2GameRenderer declares it.
  readonly renderer: RendererPort = this.r2Enabled ? new R2GameRenderer(this.audio) : new PixiRenderer();
  /** The opt-in sampler belongs only to the mobile R2 route; `?r2=0` stays untouched. */
  readonly performanceSampler = this.mobilePerformanceMode
    ? createMobileFramePhaseSampler()
    : null;
  readonly ui = new RuntimeUIStore();
  /**
   * コンボ体感層は同じフラグに従う。旧経路では生成しない（挙動同一・ルール4）。
   * Fix 1: audio を渡し、CombatAudioEngine が AudioEngine と同じ AudioContext を
   * 共有する（二重 AudioContext 常時稼働による音声スレッド負荷を解消）。
   */
  readonly comboFeel: ComboFeelAdapter | null = this.r2Enabled ? new ComboFeelAdapter(this.audio) : null;
  private readonly stageRankings = this.r2Enabled ? new StageRankingStore() : null;
  readonly loop = new FixedStepLoop(
    this.world,
    this.input,
    this.renderer,
    this.audio,
    this.ui,
    this.comboFeel,
    this.performanceSampler,
    this.mobilePerformanceMode,
  );
  settings: GameSettings = this.r2Enabled
    ? { ...DEFAULT_SETTINGS, music: R2_DEFAULT_MUSIC_LEVEL, sfx: R2_DEFAULT_SFX_LEVEL }
    : { ...DEFAULT_SETTINGS };
  private canvas: HTMLCanvasElement | null = null;
  private initialized = false;
  private disposed = false;
  private stageAdvancePending = false;
  private stageAdvanceGeneration = 0;
  private runStartPending = false;
  private runStartGeneration = 0;

  constructor() {
    if (this.r2Enabled) {
      this.world.stageDirector.setTimelineScale(R2_STAGE_TIMELINE_SCALE);
      this.world.bosses.setTuning(
        R2_BOSS_HP_MULTIPLIER,
        R2_BOSS_ATTACK_INTERVAL_SCALE,
        R2_BOSS_PROJECTILE_SPEED_SCALE,
      );
      this.world.setFinalStageResultEnabled(true);
    }
  }

  async init(canvas: HTMLCanvasElement): Promise<void> {
    if (this.initialized || this.disposed) return;
    this.world.setContinuousArsenalEnabled(this.r2Enabled);
    this.world.godMode = isGodModeEnabled();
    this.canvas = canvas;
    this.input.attach(window);
    window.addEventListener('keydown', this.onGodDebugKeyDown, { passive: false });
    // r2 経路ではリッチ SE（GameplaySfxDirector）が担当し、旧 AudioEngine は BGM
    // 専任 + フォールバックに退く。comboFeel が null（?r2=0）のときは従来通り全 SE
    // が旧経路へ落ちる。
    this.world.setSfxSink((id) => {
      const handled = this.audio.usesHTMLMediaFallback
        ? false
        : this.comboFeel?.playSfx(id, this.world.player.chain, this.world.nodes.formation) ?? false;
      if (!handled) {
        this.audio.playSfx(id);
        return;
      }
      const duck = LEGACY_DUCK_BY_SFX_ID.get(id);
      if (duck) this.audio.duckMusicExternal(duck.target, duck.duration);
    });
    this.world.setWorldEventSink((event) => this.onWorldEvent(event));
    this.audio.onStateChange = (state) => {
      this.ui.setAudioState(state);
      this.publishNow();
    };
    try {
      await this.renderer.init(canvas);
      if (this.disposed) return;
      this.ui.setRendererName(this.renderer.rendererName);
      this.renderer.setSettings(this.settings);
      this.audio.setSettings(this.settings);
      this.comboFeel?.setSettings(this.settings);
      this.world.mode = GameMode.Title;
      canvas.addEventListener('pointerdown', this.onCanvasPointerDown, { passive: false });
      canvas.addEventListener('touchstart', this.onCanvasTouchStart, { passive: false });
      canvas.addEventListener('contextmenu', this.onCanvasContextMenu, { passive: false });
      // 追従とボタン解放は canvas 外へ出ても追える必要があるので window 側で拾う。
      window.addEventListener('pointermove', this.onWindowPointerMove);
      window.addEventListener('pointerup', this.onWindowPointerUp);
      window.addEventListener('pointercancel', this.onWindowPointerUp);
      this.initialized = true;
      this.publishNow();
      this.loop.start();
    } catch (error) {
      if (this.disposed) return;
      this.loop.stop();
      this.input.dispose();
      this.audio.dispose();
      this.renderer.dispose();
      const message = error instanceof Error ? error.message : String(error);
      this.ui.setError(message);
      this.world.mode = GameMode.Error;
      this.publishNow();
    }
  }

  async unlockAudio(): Promise<boolean> {
    if (this.disposed) return false;
    let ready = false;
    try {
      await this.audio.ensureStarted();
      this.ui.setAudioState(this.audio.state);
      ready = this.audio.ready;
    } catch (error) {
      if (this.disposed) return false;
      const message = error instanceof Error ? error.message : String(error);
      this.ui.setAudioState(`error: ${message}`);
    }
    if (this.disposed) return false;
    // BGM出力が検証できてからリッチSEのサンプル群をバックグラウンド準備する。
    // 30ファイルのデコード待ちでSOUND ONの成功表示を遅らせない。
    if (ready && this.comboFeel && !this.audio.usesHTMLMediaFallback) {
      void this.comboFeel.unlock().catch(() => undefined);
    }
    this.publishNow();
    return ready;
  }

  startGame(difficulty: DifficultyId): void {
    this.invalidateStageAdvance();
    // Desktop R2 and `?r2=0` keep the established synchronous command path.
    if (!this.mobilePerformanceMode) {
      this.commitRunStart(difficulty);
      return;
    }
    if (this.runStartPending) return;
    const prepare = this.renderer.prepareForRunStart;
    if (!prepare) {
      this.commitRunStart(difficulty);
      return;
    }
    this.runStartPending = true;
    const generation = ++this.runStartGeneration;
    const expectedMode = this.world.mode;
    void prepare.call(this.renderer).then(
      () => this.finishMobileRunStart(generation, expectedMode, difficulty),
      () => this.finishMobileRunStart(generation, expectedMode, difficulty),
    );
  }

  private commitRunStart(
    difficulty: DifficultyId,
    expectedMode = this.world.mode,
  ): void {
    if (this.disposed || this.world.mode !== expectedMode) return;
    this.world.startNewGame(difficulty);
    this.audio.setPaused(false);
    this.loop.resetClock();
    this.publishNow();
  }

  private finishMobileRunStart(
    generation: number,
    expectedMode: GameMode,
    difficulty: DifficultyId,
  ): void {
    if (generation !== this.runStartGeneration) return;
    this.runStartPending = false;
    this.commitRunStart(difficulty, expectedMode);
  }

  private invalidateRunStart(): void {
    this.runStartGeneration += 1;
    this.runStartPending = false;
  }

  openControls(): void {
    this.invalidateRunStart();
    this.world.mode = GameMode.Controls;
    this.publishNow();
  }

  returnToTitle(): void {
    this.invalidateStageAdvance();
    this.invalidateRunStart();
    this.world.returnToTitle();
    this.audio.setPaused(false);
    this.loop.resetClock();
    this.publishNow();
  }

  pause(): void {
    if (this.world.mode !== GameMode.Playing) return;
    this.world.mode = GameMode.Paused;
    this.audio.setPaused(true);
    this.publishNow();
  }

  resume(): void {
    this.world.resume();
    this.audio.setPaused(false);
    this.loop.resetClock();
    this.publishNow();
  }

  selectPurchase(itemId: number): void {
    if (this.world.mode !== GameMode.CalibrationGate) return;
    this.world.setGateSelection(itemId);
    this.publishNow();
  }

  purchase(itemId = this.world.gateSelection): boolean {
    this.world.setGateSelection(itemId);
    const result = this.world.purchase(itemId);
    this.publishNow();
    return result;
  }

  leaveGate(): void {
    this.world.leaveCalibrationGate();
    this.loop.resetClock();
    this.publishNow();
  }

  advanceStage(): void {
    // Desktop R2 and `?r2=0` retain the established synchronous command path.
    if (!this.mobilePerformanceMode) {
      this.commitStageAdvance();
      return;
    }
    if (this.stageAdvancePending) return;
    const prepare = this.renderer.prepareForStageAdvance;
    if (!prepare) {
      this.commitStageAdvance();
      return;
    }
    this.stageAdvancePending = true;
    const generation = ++this.stageAdvanceGeneration;
    const expectedStage = this.world.currentStage;
    // Advance only after decoded textures and their GPU upload are ready.
    // Failure clears the pending latch so a later user action can retry while
    // the current result screen remains safe and fully rendered.
    void prepare.call(this.renderer).then(
      () => this.finishMobileStageAdvance(generation, expectedStage),
      () => this.cancelMobileStageAdvance(generation),
    );
  }

  private commitStageAdvance(expectedStage = this.world.currentStage): void {
    if (this.disposed || this.world.mode !== GameMode.StageResult) return;
    if (this.world.currentStage !== expectedStage) return;
    this.world.advanceStage();
    this.loop.resetClock();
    this.publishNow();
  }

  private finishMobileStageAdvance(generation: number, expectedStage: number): void {
    if (generation !== this.stageAdvanceGeneration) return;
    this.stageAdvancePending = false;
    this.commitStageAdvance(expectedStage);
  }

  private cancelMobileStageAdvance(generation: number): void {
    if (generation !== this.stageAdvanceGeneration) return;
    this.stageAdvancePending = false;
  }

  private invalidateStageAdvance(): void {
    this.stageAdvanceGeneration += 1;
    this.stageAdvancePending = false;
  }

  continueRun(): void {
    this.invalidateStageAdvance();
    this.world.continueRun();
    this.audio.setPaused(false);
    this.loop.resetClock();
    this.publishNow();
  }

  retryStage(): void {
    this.invalidateStageAdvance();
    this.world.retryStage();
    this.audio.setPaused(false);
    this.loop.resetClock();
    this.publishNow();
  }

  applySettings(settings: GameSettings): void {
    this.settings = { ...settings };
    this.audio.setSettings(settings);
    this.renderer.setSettings(settings);
    this.comboFeel?.setSettings(settings);
    this.publishNow();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.invalidateStageAdvance();
    this.invalidateRunStart();
    this.loop.stop();
    this.input.dispose();
    this.audio.onStateChange = null;
    this.audio.dispose();
    this.comboFeel?.dispose();
    this.renderer.dispose();
    window.removeEventListener('keydown', this.onGodDebugKeyDown);
    window.removeEventListener('pointermove', this.onWindowPointerMove);
    window.removeEventListener('pointerup', this.onWindowPointerUp);
    window.removeEventListener('pointercancel', this.onWindowPointerUp);
    const canvas = this.canvas;
    if (canvas) {
      canvas.removeEventListener('pointerdown', this.onCanvasPointerDown);
      canvas.removeEventListener('touchstart', this.onCanvasTouchStart);
      canvas.removeEventListener('contextmenu', this.onCanvasContextMenu);
    }
    this.canvas = null;
  }

  private publishNow(): void {
    // コマンド経由の world 変更（開始・タイトル復帰・ステージ進行）は chain を落としうる。
    // ループは ui.publish を直接呼ぶので、ここでの再同期は tick 由来の検出を潰さない。
    // ただし unlockAudio 経由で入力のたびに到達する。sync は発音も phase 変更もしないこと。
    this.comboFeel?.sync(this.world);
    this.ui.publish(this.world, this.input, this.loop.clock.droppedFrames, 0);
  }

  /**
   * 武器デバッグキー。装備の巡回は world 側の god ガードつきメソッドが担い、ここは
   * キー割り当てと「入力欄に打っている最中は無視する」判定だけを持つ。
   * プレイ中に限るのは、ゲート/メニューが同じキーを列選択に使う経路と競合させないため。
   */
  private readonly onGodDebugKeyDown = (event: KeyboardEvent): void => {
    if (!this.world.godMode || this.world.mode !== GameMode.Playing) return;
    if (event.repeat || event.metaKey || event.ctrlKey || event.altKey) return;
    if (isNativeUiControl(event.target)) return;
    const core = GOD_CORE_KEY[event.code];
    if (core !== undefined) this.world.setGodCore(core);
    else if (event.code === GOD_LENS_KEY) this.world.cycleGodLens();
    else if (event.code === GOD_DEVICE_KEY) this.world.cycleGodDevice();
    else if (event.code === GOD_WEAPON_LEVEL_KEY) this.world.cycleGodWeaponLevel();
    else return;
    event.preventDefault();
    this.publishNow();
  };

  private readonly onCanvasPointerDown = (event: PointerEvent): void => {
    if (event.pointerType === 'touch') return;
    if (this.world.mode === GameMode.CalibrationGate) {
      if (!this.input.notePointer()) return;
      event.preventDefault();
      this.activateGateAt(event.clientX, event.clientY);
      return;
    }
    if (this.world.mode !== GameMode.Playing || event.button !== PRIMARY_POINTER_BUTTON) return;
    event.preventDefault();
    this.setPointerTargetAt(event.clientX, event.clientY);
    this.input.setPointerFire(true);
  };

  private readonly onWindowPointerMove = (event: PointerEvent): void => {
    if (event.pointerType === 'touch') return;
    this.setPointerTargetAt(event.clientX, event.clientY);
  };

  private readonly onWindowPointerUp = (): void => {
    this.input.setPointerFire(false);
  };

  /** 右クリックのメニューは誤操作なので Playing 中だけ抑止する（機能割当なし）。 */
  private readonly onCanvasContextMenu = (event: MouseEvent): void => {
    if (this.world.mode !== GameMode.Playing) return;
    event.preventDefault();
  };

  /** client 座標 → canvas 比率 → sim 座標。×2.5 はここから先へ持ち込まない。 */
  private setPointerTargetAt(clientX: number, clientY: number): void {
    const canvas = this.canvas;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    const x = clamp(((clientX - rect.left) / rect.width) * INTERNAL_WIDTH, 0, INTERNAL_WIDTH);
    const y = clamp(((clientY - rect.top) / rect.height) * INTERNAL_HEIGHT, 0, INTERNAL_HEIGHT);
    this.input.setPointerTarget(x, y);
  }

  private readonly onCanvasTouchStart = (event: TouchEvent): void => {
    if (this.world.mode !== GameMode.CalibrationGate || event.changedTouches.length === 0) return;
    const touch = event.changedTouches[0];
    if (!touch) return;
    event.preventDefault();
    this.input.noteDirectTouch();
    this.activateGateAt(touch.clientX, touch.clientY);
  };

  private activateGateAt(clientX: number, clientY: number): void {
    const canvas = this.canvas;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    const x = ((clientX - rect.left) / rect.width) * 384;
    const y = ((clientY - rect.top) / rect.height) * 216;
    if (x < 20 || x > 364 || y < 42 || y > 183) return;
    const row = Math.max(0, Math.min(7, Math.floor((y - 45) / 17)));
    const page = this.world.gateSelection >> 3;
    const itemId = page * 8 + row;
    if (!PURCHASES[itemId]) return;
    if (this.world.gateSelection === itemId) this.world.purchase(itemId);
    else this.world.setGateSelection(itemId);
    this.publishNow();
  }

  private onWorldEvent(event: WorldEvent): void {
    if (event === WorldEvent.StageStarted) {
      this.ui.setStageRanking(null);
    } else if (event === WorldEvent.StageCleared && this.stageRankings) {
      this.audio.prepareStageMusic(this.world.currentStage + 1);
      this.ui.setStageRanking(this.stageRankings.record(
        this.world.difficulty,
        this.world.currentStage,
        this.world.stageScore,
      ));
    }
    if (!this.settings.rumble) return;
    if (event === WorldEvent.PlayerDestroyed) this.rumble(180, 0.25, 0.7);
    else if (event === WorldEvent.BossDefeated) this.rumble(420, 0.55, 0.9);
    else if (event === WorldEvent.EndingReached) this.rumble(280, 0.25, 0.45);
  }

  private rumble(duration: number, weak: number, strong: number): void {
    const pads = navigator.getGamepads?.();
    const pad = pads?.[0];
    const actuator = pad?.vibrationActuator;
    if (!actuator || typeof actuator.playEffect !== 'function') return;
    void actuator.playEffect('dual-rumble', {
      startDelay: 0,
      duration,
      weakMagnitude: weak,
      strongMagnitude: strong,
    }).catch(() => undefined);
  }
}
